import React, { useEffect, useState } from 'react';
import { useContext } from 'react';

import { Link, useNavigate, useParams } from 'react-router-dom';

import { ArrowLeftOutlined } from '@ant-design/icons';

import confetti from '../assets/images/confetti.gif';
import styles from '../assets/styles/DrawDown.module.scss';
import Breadcrumb, { BreadcrumbItem } from '../components/Breadcrumb';
import CreditLinePageTitle from '../components/CreditLine/CreditLinePageTitle';
import Button from '../components/common/Button';
import Layout from '../components/common/Layout';
import { UserContext } from '../context/UserContext';
import { formatUSDCurrency } from '../helpers/format';
import { getLoanDetails, getLoans, sendDrawDown, sendDrawDownConfirmation } from '../service/loan';

const DrawDown = () => {
  const [loans, setLoans] = useState([]);
  const [amount, setAmount] = useState('$');
  const [inputError, setInputError] = useState('');
  const [drawDownResponse, setDrawDownResponse] = useState(null);
  const [drawDownConfirmationResponse, setDrawDownConfirmationResponse] = useState(null);
  const [loanData, setLoanData] = useState(null);
  const [isConfirmation, setIsConfirmation] = useState(false);
  const { loading, setLoading } = useContext(UserContext);
  const params = useParams();
  const loanId = params.id;
  const loan = loans?.find((loan) => loan.id === loanId);
  const navigate = useNavigate();

  useEffect(() => {
    const loadLoans = async () => {
      setLoading(true);
      try {
        const {
          data: { data },
        } = await getLoans();

        setLoans(data);
      } catch (e) {
        console.log(e);
        navigate('/dashboard');
      }
    };
    loadLoans();
  }, []);

  useEffect(() => {
    const loadLoan = async (id) => {
      setLoading(true);
      try {
        const { data } = await getLoanDetails(id);
        setLoanData(data.lineOfCreditDto);
      } catch (e) {
        console.log('error', e);
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    if (loans.length > 0) loadLoan(loans?.find((loan) => loan.id === loanId).servicingLoanId);
  }, [loans]);

  const inputValidation = () => {
    if (amount.toString().trim() === '$') {
      setInputError('A draw down amount is required.');
      return false;
    } else if (amount.replaceAll(',', '').replaceAll('$', '') > loanData.availableToDraw) {
      return false;
    }
    return true;
  };

  const drawDownConfirmation = async () => {
    const servicingLoanId = loan.servicingLoanId;
    setLoading(true);
    try {
      const res = await sendDrawDownConfirmation(servicingLoanId, {
        paymentAmount: drawDownResponse?.requestedDrawAmount,
        disbursedAmount: drawDownResponse?.disbursedAmount,
      });
      setDrawDownConfirmationResponse(res.data);
    } catch (e) {
      console.log(e);
      setIsConfirmation(false);
      setInputError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const makeDrawDown = async () => {
    const servicingLoanId = loans?.find((loan) => loan.id === loanId).servicingLoanId;
    setLoading(true);
    try {
      const response = await sendDrawDown(servicingLoanId, {
        paymentAmount: amount.replaceAll(',', '').replaceAll('$', ''),
      });
      setDrawDownResponse(response.data);
    } catch (e) {
      console.log(e);
      setIsConfirmation(false);
      setInputError(e.response?.data?.errors[0]?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    if (inputValidation()) {
      makeDrawDown();
      setIsConfirmation(true);
    }
  };
  const handleChangeAmount = (e) => {
    setInputError('');
    let val = parseInt(e.target.value.replaceAll(',', '').replaceAll('$', ''), 10);
    if (isNaN(val)) {
      setAmount('$');
    } else {
      if (val < 1) {
        val = 1;
      } else {
        if (val > loanData.availableToDraw) {
          setInputError(`The amount entered is over the draw limit`);
        }
      }
      setAmount(formatUSDCurrency(val));
    }
  };

  const inputStyle = `${inputError !== '' && styles.inputOnError}`;
  const labelStyle = `${inputError !== '' && styles.labelOnError}`;

  const renderContent = () => {
    return !isConfirmation && !loading && loanData ? (
      <div className={styles.DrawDown}>
        {!inputError ? (
          <Breadcrumb className={styles.DrawDown__breadcrumb}>
            <BreadcrumbItem>
              <Link to={`/dashboard`}>Line of Credit</Link>
            </BreadcrumbItem>
            <BreadcrumbItem>Draw down</BreadcrumbItem>
          </Breadcrumb>
        ) : (
          <div className={styles.backOnError} onClick={() => navigate('/dashboard')}>
            <ArrowLeftOutlined
              style={{
                strokeWidth: '25',
                stroke: '#000',
                fontSize: '16px',
                height: '19.2px',
              }}
            />
            <h3 className={styles.tableTitle}>Back</h3>
          </div>
        )}
        <div className={styles.DrawDown__contentBox}>
          <CreditLinePageTitle customClass={styles.pageTitle}>Draw Down</CreditLinePageTitle>
          <div className={styles.DrawDown__contentBox_amountInput}>
            <label
              className={labelStyle}
              htmlFor="amount"
            >{`Enter amount - Max: ${formatUSDCurrency(loanData.availableToDraw, 2, true)}`}</label>
            <input
              className={inputStyle}
              id="amount"
              value={amount}
              onChange={(e) => handleChangeAmount(e)}
            />
            {inputError !== '' && <p>{inputError}</p>}
          </div>

          <div className={styles.DrawDown__contentBox_agreement}></div>

          <Button
            customClass={styles.DrawDown__button}
            onClick={() => handleContinue()}
            type="primary"
          >
            Continue
          </Button>
        </div>
      </div>
    ) : (
      <>
        {drawDownResponse && (
          <div className={styles.DrawDown}>
            <Breadcrumb className={styles.DrawDown__breadcrumb}>
              <BreadcrumbItem>
                <Link to={`/dashboard`}>Line of Credit</Link>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <span
                  style={{ cursor: 'pointer', color: '#462d7a' }}
                  onClick={() => {
                    setIsConfirmation(false);
                    setDrawDownResponse(null);
                    setDrawDownConfirmationResponse(null);
                  }}
                >
                  Draw down
                </span>
              </BreadcrumbItem>
              <BreadcrumbItem>Confirmation</BreadcrumbItem>
            </Breadcrumb>
            <div className={styles.DrawDown__contentBox}>
              {!drawDownConfirmationResponse && !loading && (
                <>
                  <CreditLinePageTitle customClass={styles.pageTitle}>
                    Drawdown Confirmation
                  </CreditLinePageTitle>
                  <div className={styles.DrawDown__contentBox_confirmation}>
                    <div>
                      <p>Amount Requested:</p>
                      <p>
                        <strong>
                          {formatUSDCurrency(drawDownResponse.requestedDrawAmount, 2, true)}
                        </strong>
                      </p>
                    </div>
                    <div>
                      <p>Drawdown Fee:</p>
                      <p>
                        <strong>{formatUSDCurrency(drawDownResponse.drawFees, 2, true)}</strong>
                      </p>
                    </div>
                    <div>
                      <p>Interest Reserve:</p>
                      <p>
                        <strong>
                          {formatUSDCurrency(drawDownResponse.reserveAmount, 2, true)}
                        </strong>
                      </p>
                    </div>
                    <div>
                      <p>
                        <strong>Disbursed Proceeds:</strong>
                      </p>
                      <p className={styles.filledBackground}>
                        <strong>
                          {formatUSDCurrency(drawDownResponse.disbursedAmount, 2, true)}
                        </strong>
                      </p>
                    </div>
                  </div>

                  <Button
                    customClass={styles.DrawDown__button}
                    onClick={() => drawDownConfirmation()}
                    type="primary"
                  >
                    Confirm
                  </Button>
                </>
              )}
              {drawDownConfirmationResponse && (
                <>
                  <CreditLinePageTitle customClass={styles.pageTitle}>Congrats</CreditLinePageTitle>
                  <img src={confetti} alt="Congrats" className={styles.confetti} />
                  <p
                    className={`${styles.DrawDown__contentBox_description} ${styles.congrats_description}`}
                  >
                    Your draw down of{' '}
                    <strong>
                      {formatUSDCurrency(drawDownConfirmationResponse.requestedDrawAmount, 2, true)}
                    </strong>{' '}
                    was successful
                  </p>

                  <Button
                    customClass={styles.DrawDown__button}
                    onClick={() => {
                      navigate('/dashboard');
                    }}
                    type="primary"
                  >
                    Return Home
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </>
    );
  };

  return <Layout>{renderContent()}</Layout>;
};

export default DrawDown;
